/**
 * Performance controls.
 *
 * This renderer is built to a "stand next to a modern Call of Duty" bar, and it
 * costs what that implies: the `ultra` preset is four 4096 shadow cascades,
 * GTAO, SSR, TAA, motion blur and volumetrics over a 1.6 M-triangle street.
 * Upstream's own measurements put it at 28-30 fps on an Apple silicon laptop.
 * Shipping that as the default on the open web means most visitors get a
 * slideshow and never find out there were other presets.
 *
 * So: a visible control, not a URL parameter you have to already know about.
 *
 *   resolution scale  LIVE. The single biggest lever, and it only reallocates
 *                     render targets — no reload, no state lost.
 *   quality preset    RELOAD. The preset is consumed when RenderSystem builds
 *                     its pipeline objects (cascade count, whether an SSR pass
 *                     exists at all), so changing it means rebuilding them.
 *   frame cap         LIVE. On a 144 Hz panel an uncapped loop will happily
 *                     spend the whole GPU to draw frames nobody asked for.
 */

const PRESETS = ['low', 'medium', 'high', 'ultra'];
const CAPS = [0, 30, 60, 72, 90, 120, 144];

const PANEL_CSS = `
  position: fixed;
  left: 20px;
  bottom: 20px;
  z-index: 10000;
  display: grid;
  gap: 9px;
  padding: 14px 16px;
  min-width: 232px;
  border: 1px solid rgba(180, 205, 215, 0.34);
  border-radius: 10px;
  background: rgba(10, 14, 16, 0.86);
  color: #dbe9e5;
  font: 500 12px/1.35 ui-monospace, "SF Mono", Menlo, monospace;
  letter-spacing: 0.05em;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
`;

const TOGGLE_CSS = `
  position: fixed;
  left: 20px;
  bottom: 20px;
  z-index: 10000;
  width: 38px;
  height: 38px;
  border: 1px solid rgba(180, 205, 215, 0.34);
  border-radius: 10px;
  background: rgba(10, 14, 16, 0.78);
  color: #dbe9e5;
  font: 600 15px/1 ui-monospace, monospace;
  cursor: pointer;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
`;

const ROW = 'display:flex;align-items:center;justify-content:space-between;gap:10px;';
const LABEL = 'color:rgba(170,190,200,0.72);text-transform:uppercase;font-size:10px;';
const INPUT =
  'background:rgba(0,0,0,0.45);color:#dbe9e5;border:1px solid rgba(180,205,215,0.28);' +
  'border-radius:5px;padding:3px 6px;font:500 11px ui-monospace,monospace;';

/**
 * @param {import('./engine.js').Engine} engine
 */
export function installPerfPanel(engine) {
  const render = engine.registry.peek('render');
  if (!render) return null;

  const params = new URLSearchParams(location.search);
  let panelWarn = null;

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.style.cssText = TOGGLE_CSS;
  toggle.textContent = '⚙';
  toggle.title = 'Performance';

  const panel = document.createElement('div');
  panel.style.cssText = PANEL_CSS;
  panel.style.display = 'none';

  // ---- what is actually drawing this ------------------------------------
  // The single most useful number on this panel. If the browser has fallen
  // back to SwiftShader (Chrome/Edge's software GL) then no preset, resolution
  // or frame cap will help: every triangle is being rasterised on the CPU, and
  // the correct fix is turning hardware acceleration back on, not lowering
  // settings. That failure mode looks exactly like "the game is too heavy",
  // which is why it is worth surfacing rather than leaving in about:gpu.
  const gpuRow = document.createElement('div');
  gpuRow.style.cssText = 'display:grid;gap:2px;';
  const gpuLabel = document.createElement('span');
  gpuLabel.style.cssText = LABEL;
  gpuLabel.textContent = 'renderer';
  const gpuValue = document.createElement('span');
  gpuValue.style.cssText = 'font-size:10px;line-height:1.4;word-break:break-word;';

  let rendererName = 'unknown';
  try {
    const gl = render.renderer.getContext();
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    rendererName =
      (dbg && gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) ||
      gl.getParameter(gl.RENDERER) ||
      'unknown';
  } catch {
    /* extension blocked by privacy settings — leave it unknown */
  }
  const software = /swiftshader|llvmpipe|softwarerasterizer|basic render|microsoft basic/i
    .test(rendererName);
  gpuValue.textContent = String(rendererName);
  gpuValue.style.color = software ? '#e0463c' : 'rgba(150,175,185,0.62)';
  gpuRow.append(gpuLabel, gpuValue);

  // Which adapter the browser bound to decides whether anything else on this
  // panel matters, and on a machine with both an integrated and a discrete GPU
  // it is very often not the one the owner assumes. Browsers cache that choice
  // at launch from a per-application OS preference, so a machine with a fast
  // card can sit at 13 fps on its iGPU indefinitely with no visible clue —
  // which reads as "the game is broken", not "the browser picked wrong".
  const integrated = /intel\(r\)? (u?hd|iris)|intel.*graphics|vega \d+ graphics|radeon graphics/i
    .test(rendererName) && !/arc|nvidia|geforce/i.test(rendererName);
  console.info(`[perf] WebGL renderer: ${rendererName}`);

  if (software || integrated) {
    const warn = document.createElement('div');
    warn.style.cssText =
      'color:#e0463c;font-size:10px;line-height:1.5;border:1px solid rgba(224,70,60,0.4);' +
      'border-radius:6px;padding:7px 8px;';
    warn.textContent = software
      ? 'SOFTWARE RENDERING — no GPU in use at all. Enable hardware acceleration ' +
        'in your browser settings; no quality preset will fix this.'
      : 'INTEGRATED GPU — if this machine also has a discrete card, the browser ' +
        'is not using it. Windows: Settings > System > Display > Graphics > ' +
        'your browser > High performance, then fully restart the browser. ' +
        'This matters far more than any setting below.';
    panelWarn = warn;
    console.warn(`[perf] ${software ? 'software renderer' : 'integrated GPU'}: ${rendererName}`);
  }

  // ---- fps readout -------------------------------------------------------
  const fpsRow = document.createElement('div');
  fpsRow.style.cssText = ROW;
  const fpsLabel = document.createElement('span');
  fpsLabel.style.cssText = LABEL;
  fpsLabel.textContent = 'fps';
  const fpsValue = document.createElement('span');
  fpsValue.style.cssText = 'font-weight:700;font-size:15px;';
  fpsValue.textContent = '--';
  fpsRow.append(fpsLabel, fpsValue);

  // ---- resolution scale (live) -------------------------------------------
  const scaleRow = document.createElement('div');
  scaleRow.style.cssText = ROW;
  const scaleLabel = document.createElement('span');
  scaleLabel.style.cssText = LABEL;
  scaleLabel.textContent = 'resolution';
  const scaleValue = document.createElement('span');
  scaleValue.style.cssText = 'min-width:38px;text-align:right;';
  const scale = document.createElement('input');
  scale.type = 'range';
  scale.min = '0.45';
  scale.max = '1';
  scale.step = '0.05';
  scale.value = String(render.q.renderScale ?? 1);
  scale.style.cssText = 'width:96px;accent-color:#7fb3a6;';
  scaleValue.textContent = `${Math.round(Number(scale.value) * 100)}%`;
  scaleRow.append(scaleLabel, scale, scaleValue);

  scale.addEventListener('input', () => {
    const v = Number(scale.value);
    scaleValue.textContent = `${Math.round(v * 100)}%`;
    render.q.renderScale = v;
    // resize() recomputes the internal target size from renderScale and
    // reallocates. Nothing else in the frame cares.
    engine.resize();
  });

  // ---- frame cap (live) --------------------------------------------------
  const capRow = document.createElement('div');
  capRow.style.cssText = ROW;
  const capLabel = document.createElement('span');
  capLabel.style.cssText = LABEL;
  capLabel.textContent = 'frame cap';
  const cap = document.createElement('select');
  cap.style.cssText = INPUT;
  for (const c of CAPS) {
    const o = document.createElement('option');
    o.value = String(c);
    o.textContent = c === 0 ? 'uncapped' : `${c} fps`;
    cap.appendChild(o);
  }
  cap.value = String(engine.frameCap);
  cap.addEventListener('change', () => {
    engine.frameCap = Number(cap.value);
  });
  capRow.append(capLabel, cap);

  // ---- preset (reload) ---------------------------------------------------
  const presetRow = document.createElement('div');
  presetRow.style.cssText = ROW;
  const presetLabel = document.createElement('span');
  presetLabel.style.cssText = LABEL;
  presetLabel.textContent = 'preset';
  const preset = document.createElement('select');
  preset.style.cssText = INPUT;
  for (const p of PRESETS) {
    const o = document.createElement('option');
    o.value = p;
    o.textContent = p;
    preset.appendChild(o);
  }
  preset.value = engine.config.quality;
  preset.addEventListener('change', () => {
    params.set('q', preset.value);
    location.search = params.toString();
  });
  presetRow.append(presetLabel, preset);

  // ---- diagnostics -------------------------------------------------------
  // Enough to tell the three failure modes apart without a profiler:
  //   internal res climbing            -> DPR/window size, lower the scale
  //   programs climbing while playing  -> shader recompilation stalls, which
  //                                       look like "5 fps in bursts" and are
  //                                       NOT a fill-rate problem
  //   calls/tris high, programs stable -> genuinely too much geometry
  const diag = document.createElement('div');
  diag.style.cssText =
    'color:rgba(150,175,185,0.62);font-size:10px;line-height:1.6;' +
    'border-top:1px solid rgba(180,205,215,0.18);padding-top:8px;';

  const note = document.createElement('div');
  note.style.cssText = 'color:rgba(150,175,185,0.48);font-size:10px;line-height:1.5;';
  note.textContent = 'resolution + cap apply instantly · preset reloads';

  // ---- one-click floor ---------------------------------------------------
  // Somebody whose machine is struggling should not have to learn what a
  // render scale is before the game is playable. This is the bottom of every
  // lever at once: lowest preset, half resolution, 60 Hz cap.
  const rescue = document.createElement('button');
  rescue.type = 'button';
  rescue.textContent = 'MAKE IT RUN';
  rescue.style.cssText =
    INPUT +
    'cursor:pointer;letter-spacing:0.12em;font-weight:700;padding:8px;' +
    'border-color:rgba(143,214,190,0.5);color:#8fd6be;';
  rescue.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    params.set('q', 'low');
    params.set('scale', '0.5');
    params.set('fps', '60');
    location.search = params.toString();
  });

  const close = document.createElement('button');
  close.type = 'button';
  close.textContent = 'CLOSE';
  close.style.cssText =
    INPUT + 'cursor:pointer;letter-spacing:0.12em;font-weight:600;padding:6px;';

  panel.append(gpuRow, fpsRow, scaleRow, capRow, presetRow, diag, note, close);
  if (panelWarn) panel.insertBefore(panelWarn, fpsRow);

  const setOpen = (open) => {
    panel.style.display = open ? 'grid' : 'none';
    toggle.style.display = open ? 'none' : 'block';
  };
  toggle.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  });
  close.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
  });
  // The canvas grabs pointer lock on mousedown; keep clicks in here local.
  for (const el of [panel, toggle]) {
    el.addEventListener('mousedown', (e) => e.stopPropagation());
  }

  document.body.append(toggle, panel);

  // ---- fps meter + diagnostics -------------------------------------------
  // Worst frame in the window matters more than the mean: "5 fps in bursts" is
  // a p100 problem (one 200 ms stall) wearing a p50 costume, and averaging hides
  // exactly the thing you need to see.
  let acc = 0;
  let frames = 0;
  let worst = 0;
  let last = performance.now();
  let programsBaseline = -1;
  const tick = () => {
    const now = performance.now();
    const frameMs = now - last;
    acc += frameMs;
    if (frameMs > worst) worst = frameMs;
    last = now;
    frames++;

    if (acc >= 500) {
      const fps = (frames * 1000) / acc;
      fpsValue.textContent = `${fps.toFixed(0)}  (worst ${worst.toFixed(0)}ms)`;
      fpsValue.style.color = fps < 30 ? '#e0463c' : fps < 55 ? '#e0b23c' : '#8fd6be';

      const info = render.renderer.info;
      const programs = info.programs?.length ?? 0;
      if (programsBaseline < 0) programsBaseline = programs;
      // Programs are only compiled on demand. A count that keeps climbing after
      // boot means the frame loop is hitting new shader permutations, and each
      // one is a multi-hundred-millisecond stall on the main thread.
      const drift = programs - programsBaseline;

      const ss = render.screenSize;
      const ds = render.displaySize;
      diag.innerHTML = '';
      const line = (k, v, warn) => {
        const d = document.createElement('div');
        d.style.cssText = ROW;
        const a = document.createElement('span');
        a.style.cssText = LABEL;
        a.textContent = k;
        const b = document.createElement('span');
        b.textContent = v;
        if (warn) b.style.color = '#e0463c';
        d.append(a, b);
        diag.appendChild(d);
      };
      const mp = (ss.width * ss.height) / 1e6;
      line('internal', `${ss.width}×${ss.height} (${mp.toFixed(1)} MP)`, mp > 4);
      line('display', `${ds.width}×${ds.height} · dpr ${(globalThis.devicePixelRatio || 1).toFixed(2)}`);
      line('draw calls', String(info.render?.calls ?? 0));
      line('triangles', ((info.render?.triangles ?? 0) / 1e6).toFixed(2) + ' M');
      line('programs', drift > 0 ? `${programs}  (+${drift} since boot)` : String(programs), drift > 0);
      line('preset', engine.config.quality);

      acc = 0;
      frames = 0;
      worst = 0;
    }
    raf = requestAnimationFrame(tick);
  };
  let raf = requestAnimationFrame(tick);

  return {
    dispose() {
      cancelAnimationFrame(raf);
      toggle.remove();
      panel.remove();
    },
  };
}
